// ./src/pages/Home/components/FAQSection.js
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    question: "What is DeltaOps?",
    answer: "DeltaOps is a simple but powerful change tracking system that helps engineering teams identify the root cause of incidents faster. Instead of digging through logs and Git history during an outage, you can quickly search your system's change timeline to find what caused the problem."
  },
  {
    question: "How does it work?",
    answer: "DeltaOps provides a semantic search engine that integrates with your existing workflow. For GitHub users, our app automatically tracks all commits and changes. For other systems (deployments, configuration updates, database migrations, etc.), you can send changes via our API. When something goes wrong, you can quickly search through your change history to find what might have caused the issue."
  },
  {
    question: "What kinds of changes can I track?",
    answer: "You can track any type of change that happens in your system. Common examples include: code deployments, configuration changes, database migrations, infrastructure updates, and system restarts. Each change event can include metadata to help you understand exactly what changed and why."
  },
  {
    question: "How do I get started?",
    answer: "Getting started is incredibly easy. Simply install our GitHub App and select the repositories you want to track. DeltaOps will automatically ingest all commits from your selected repositories' target branches and turn them into searchable changelog events. You can also integrate with other systems using our API."
  },
  {
    question: "Does DeltaOps store my source code or proprietary information?",
    answer: "No, DeltaOps does not store your actual source code. We only collect metadata about changes, such as descriptions, timestamps, and links to your pull requests or deployment records. This ensures that your code remains secure and private while allowing DeltaOps to provide insightful incident analysis."
  },
  {
    question: "What data does DeltaOps collect?",
    answer: "DeltaOps collects metadata related to system changes, including descriptions of changes, timestamps, user information, and links to related pull requests or deployment records. We do not store any actual code or sensitive proprietary information."
  },
  {
    question: "How does DeltaOps ensure the security and privacy of my data?",
    answer: "We implement robust security measures, including encryption of data in transit and at rest. Access to your data is strictly controlled, and we adhere to industry-standard security practices to safeguard your information."
  }
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div 
      className={`border-b border-gray-200 last:border-0 transition-colors ${
        isOpen ? 'bg-gray-50' : 'hover:bg-gray-50'
      }`}
    >
      <button
        className="w-full py-6 px-6 text-left flex items-center justify-between focus:outline-none"
        onClick={onToggle}
      >
        <h3 className="text-lg font-medium text-gray-900">{question}</h3>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 text-gray-600">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (questionId) => {
    setOpenItems(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  return (
    <div id="faq" className="bg-gradient-to-b from-white to-gray-50 py-24">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Common Questions</h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about tracking your system changes
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openItems[index]}
              onToggle={() => toggleItem(index)}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Have more questions?{' '}
            <a 
              href="mailto:marcus@deltaops.app" 
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Email us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
